import React, { useRef, useState } from 'react';
import { Camera, CameraResultType, CameraSource, CameraDirection } from '@capacitor/camera';
import { isAndroidCapacitor } from '../../utils/platformUtils';
import BottomSheet from './BottomSheet';
import './ImagePicker.css';

const ImagePicker = ({ onFileReady, buttonText = "Add photo", className = "" }) => {
  const fileInputRef = useRef(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [error, setError] = useState('');

  // Helper function to convert base64 to File
  const base64ToFile = (base64, filename) => {
    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], filename, { type: 'image/jpeg' });
  };

  // Helper function to generate filename
  const makeImageFileName = () => {
    return `photo_${Date.now()}.jpg`;
  };

  // Handle camera capture
  const handleTakePhoto = async () => {
    try {
      setError('');
      setIsBottomSheetOpen(false);
      
      const photo = await Camera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Base64,
        quality: 80,
        direction: CameraDirection.Rear, // force back camera
        correctOrientation: true,
        saveToGallery: false
      });

      if (photo.base64String) {
        const file = base64ToFile(photo.base64String, makeImageFileName());
        onFileReady(file);
      }
    } catch (error) {
      console.log('Camera cancelled or permission denied:', error.message);
      // Don't show error for user cancellation
      if (error.message !== 'User cancelled photos app') {
        setError('Failed to take photo. Please try again.');
      }
    }
  };

  // Handle gallery selection
  const handleChooseFromGallery = async () => {
    try {
      setError('');
      setIsBottomSheetOpen(false);
      
      const photo = await Camera.getPhoto({
        source: CameraSource.Photos,
        resultType: CameraResultType.Base64,
        quality: 80,
        correctOrientation: true
      });

      if (photo.base64String) {
        const file = base64ToFile(photo.base64String, makeImageFileName());
        onFileReady(file);
      }
    } catch (error) {
      console.log('Gallery cancelled or permission denied:', error.message);
      // Don't show error for user cancellation
      if (error.message !== 'User cancelled photos app') {
        setError('Failed to select photo. Please try again.');
      }
    }
  };

  // Handle web file input
  const handleWebFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setError('');
      onFileReady(file);
    }
  };

  // Handle click based on platform
  const handleClick = () => {
    setError('');
    if (isAndroidCapacitor()) {
      setIsBottomSheetOpen(true);
    } else {
      // Web platform - trigger file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
    }
  };

  return (
    <div className={`image-picker ${className}`}>
      <button 
        type="button"
        className="image-picker-button"
        onClick={handleClick}
      >
        {buttonText}
      </button>
      
      {error && (
        <div className="image-picker-error">
          {error}
        </div>
      )}

      {/* Hidden file input for web */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleWebFileSelect}
        style={{ display: 'none' }}
      />

      {/* Bottom sheet for Android */}
      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
        onTakePhoto={handleTakePhoto}
        onChooseFromGallery={handleChooseFromGallery}
      />
    </div>
  );
};

export default ImagePicker;
